/**
 * posthog-js init config consumed once by `utils/analytics-provider.tsx`.
 * Session replay masks passwords and URL credentials, and is disabled outside
 * prod/preview. Marketing PostHog is configured separately in `AnalyticsIdentify.tsx`
 * and shares `SHARED_PRIVACY_CONFIG`.
 */

import { clientEnv } from "@/clientEnv";
import { mapRecordEntries, notNull } from "@/utils/helpers";
import { getDeploymentRegion } from "@/utils/o11y/environment";
import {
  getPostHogUiHost,
  POSTHOG_PROXY_PREFIX,
} from "@/utils/posthog/regions";
import type posthogJs from "posthog-js";
import type {
  BeforeSendFn,
  CapturedNetworkRequest,
  Properties,
} from "posthog-js";

const CREDENTIAL_PARAM =
  /([?#&](?:access_token|refresh_token|provider_token|provider_refresh_token|token_hash|code)=)[^&#\s"']*/g;

const redactUrlCredentials = (value: string): string =>
  value.replace(CREDENTIAL_PARAM, "$1REDACTED");

const isPlainObject = (value: unknown): value is Properties =>
  notNull(value) && Object.getPrototypeOf(value) === Object.prototype;

const redactValue = (value: unknown): unknown => {
  if (typeof value === "string") return redactUrlCredentials(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (isPlainObject(value)) return redactProperties(value);
  return value;
};

// Keys too: heatmap events key their data by page URL.
const redactProperties = (properties: Properties): Properties =>
  mapRecordEntries(properties, ([key, value]) => [
    redactUrlCredentials(key),
    redactValue(value),
  ]);

const redactCaptureCredentials: BeforeSendFn = (captureResult) => {
  if (!captureResult) return null;
  const isSnapshot = captureResult.event === "$snapshot";
  return {
    ...captureResult,
    // Snapshot payloads are large, and their page URLs go through redactRecordedRequest.
    properties: isSnapshot
      ? captureResult.properties
      : redactProperties(captureResult.properties),
    $set: captureResult.$set && redactProperties(captureResult.$set),
    $set_once:
      captureResult.$set_once && redactProperties(captureResult.$set_once),
  };
};

// A custom mask fn replaces posthog-js body scrubbing, so bodies are dropped.
const redactRecordedRequest = (
  request: CapturedNetworkRequest
): CapturedNetworkRequest => ({
  ...request,
  name: redactUrlCredentials(request.name),
  requestBody: undefined,
  responseBody: undefined,
});

// The SDK logs URL changes in a hidden tab, then logs the first page view again once the tab is shown.
const dropHiddenTabNavigation: BeforeSendFn = (captureResult) => {
  const isHiddenTabNavigation =
    captureResult?.event === "$pageview" &&
    notNull(captureResult.properties.navigation_type) &&
    document.visibilityState !== "visible";
  return isHiddenTabNavigation ? null : captureResult;
};

type PostHogInitConfig = Parameters<typeof posthogJs.init>[1];

const isProductionOrPreview =
  clientEnv.NEXT_PUBLIC_VERCEL_ENV === "production" ||
  clientEnv.NEXT_PUBLIC_VERCEL_ENV === "preview";

const APP_ENV = clientEnv.NEXT_PUBLIC_VERCEL_ENV ?? "development";
const APP_VERSION = clientEnv.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;

export const POSTHOG_BASE_PROPERTIES = {
  app_env: APP_ENV,
  deployment_region: getDeploymentRegion(),
  ...(APP_VERSION ? { app_version: APP_VERSION } : {}),
};

export const SHARED_PRIVACY_CONFIG = {
  before_send: redactCaptureCredentials,
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
    maskCapturedNetworkRequestFn: redactRecordedRequest,
  },
} satisfies PostHogInitConfig;

export const CLIENT_INIT_CONFIG: PostHogInitConfig = {
  api_host: POSTHOG_PROXY_PREFIX,
  ui_host: getPostHogUiHost(),
  ...SHARED_PRIVACY_CONFIG,
  before_send: [dropHiddenTabNavigation, SHARED_PRIVACY_CONFIG.before_send],
  // Flags come from /api/feature-flags/bootstrap, not the capture project.
  advanced_disable_feature_flags: true,
  // Also logs `$pageview` on Next.js client navigation; the SDK default covers full page loads only.
  capture_pageview: "history_change",
  disable_session_recording: !isProductionOrPreview,
  // Adds the LCP element and timing split to `$web_vitals`; the project setting still decides on/off.
  capture_performance: { web_vitals_attribution: true },
};

/**
 * The theme the page is painted in. Read off the `.dark` class because callers
 * sit above ThemeProvider, and because respondent routes have none and must
 * report light rather than inherit the last workspace value. Browser only.
 */
export const getRenderedTheme = (): "light" | "dark" =>
  document.documentElement.classList.contains("dark") ? "dark" : "light";
